import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreateTeam, useUpdateTeam } from "@/hooks/queries/use-teams";
import { ApiError } from "@/lib/api/client";
import type { Team } from "@/lib/api/types";

interface DisciplineFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentId?: string;
  discipline?: Team;
}

function sanitizeTeamKey(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 10);
}

function normalizeOptionalValue(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    const data = error.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? error.message ?? fallback;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

export function DisciplineFormModal({ open, onOpenChange, parentId, discipline }: DisciplineFormModalProps) {
  const createTeam = useCreateTeam();
  const updateTeam = useUpdateTeam();
  const isEditing = Boolean(discipline);
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState("");
  const [color, setColor] = useState("");
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    setName(discipline?.name ?? "");
    setKey(discipline?.key ?? "");
    setDescription(discipline?.description ?? "");
    setIcon(discipline?.icon ?? "");
    setColor(discipline?.color ?? "");
    setKeyManuallyEdited(Boolean(discipline));
    setError("");
  }, [discipline, open]);

  const handleNameChange = (value: string) => {
    setName(value);

    if (!isEditing && !keyManuallyEdited) {
      setKey(sanitizeTeamKey(value.replace(/\s+/g, "")));
    }
  };

  const handleKeyChange = (value: string) => {
    setKey(sanitizeTeamKey(value));
    setKeyManuallyEdited(true);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    const trimmedName = name.trim();
    const trimmedKey = key.trim();
    const normalizedDescription = description.trim();
    const normalizedIcon = icon.trim();
    const normalizedColor = color.trim();

    if (!trimmedName) {
      setError("Discipline name is required");
      return;
    }

    if (!trimmedKey) {
      setError("Discipline key is required");
      return;
    }

    if (!/^[A-Z][A-Z0-9-]*$/.test(trimmedKey)) {
      setError("Key must start with an uppercase letter and contain only uppercase letters, digits, or hyphens");
      return;
    }

    if (normalizedColor && !/^#[0-9A-Fa-f]{6}$/.test(normalizedColor)) {
      setError("Color must be a valid hex value like #3B82F6");
      return;
    }

    if (!isEditing && !parentId) {
      setError("A parent product is required to create a discipline");
      return;
    }

    try {
      if (discipline) {
        const updateInput: Parameters<typeof updateTeam.mutateAsync>[0] = {
          id: discipline.id,
          name: trimmedName,
          key: trimmedKey,
          ...(normalizedDescription ? { description: normalizedDescription } : {}),
          ...(normalizedIcon ? { icon: normalizedIcon } : {}),
        };

        if (normalizedColor) {
          updateInput.color = normalizedColor;
        }

        await updateTeam.mutateAsync(updateInput);
      } else {
        const createInput: Parameters<typeof createTeam.mutateAsync>[0] = {
          name: trimmedName,
          key: trimmedKey,
          level: "discipline",
        };

        if (parentId !== undefined) {
          createInput.parentId = parentId;
        }

        const nextDescription = normalizeOptionalValue(description);
        const nextIcon = normalizeOptionalValue(icon);
        const nextColor = normalizeOptionalValue(color);

        if (nextDescription !== undefined) {
          createInput.description = nextDescription;
        }
        if (nextIcon !== undefined) {
          createInput.icon = nextIcon;
        }
        if (nextColor !== undefined) {
          createInput.color = nextColor;
        }

        await createTeam.mutateAsync(createInput);
      }

      onOpenChange(false);
    } catch (submitError) {
      setError(getErrorMessage(submitError, isEditing ? "Failed to update discipline" : "Failed to create discipline"));
    }
  };

  const isPending = createTeam.isPending || updateTeam.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={(event) => void handleSubmit(event)}>
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit Discipline" : "Add Discipline"}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? "Update the discipline metadata shown in the hierarchy and detail views."
                : "Create a new discipline under the selected product team."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {error ? <div className="text-sm text-destructive">{error}</div> : null}

            <div className="grid gap-2">
              <Label htmlFor="discipline-name">Name</Label>
              <Input
                id="discipline-name"
                value={name}
                onChange={(event) => handleNameChange(event.target.value)}
                placeholder="Platform Engineering"
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="discipline-key">Key</Label>
              <Input
                id="discipline-key"
                value={key}
                onChange={(event) => handleKeyChange(event.target.value)}
                placeholder="PLAT"
                maxLength={10}
                required
              />
              <p className="text-xs text-muted-foreground">
                Keys must start with an uppercase letter and may include digits or hyphens.
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="discipline-description">Description</Label>
              <Textarea
                id="discipline-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Owns shared platform services, tooling, and developer enablement."
                rows={4}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label htmlFor="discipline-icon">Icon</Label>
                <Input
                  id="discipline-icon"
                  value={icon}
                  onChange={(event) => setIcon(event.target.value)}
                  placeholder="wrench"
                  maxLength={50}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="discipline-color">Color</Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="discipline-color"
                    value={color}
                    onChange={(event) => setColor(event.target.value)}
                    placeholder="#3B82F6"
                    maxLength={7}
                  />
                  <span
                    className="h-10 w-10 shrink-0 rounded-md border border-border"
                    style={{ backgroundColor: /^#[0-9A-Fa-f]{6}$/.test(color) ? color : "transparent" }}
                    aria-hidden="true"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name.trim() || !key.trim()}>
              {isPending ? "Saving..." : isEditing ? "Save Discipline" : "Create Discipline"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
